import { useCallback, useEffect, useRef, useState } from "react";
import { HocuspocusProvider } from "@hocuspocus/provider";

import { getTenant } from "@/TENANT_CONFIGS";
import { getWebSocketEndpoint } from "@/webSocketDefaults.js";

/**
 * Live collab session for one content doc, against the okf-sub Hocuspocus
 * server (/collab on the sync-engine host).
 *
 * Exposes:
 *  - fields: live { [valuePath]: value } snapshot of the server-projected
 *    Y.Map — the source of truth for editable fields while connected
 *  - locks: { [valuePath]: { userId, userName, socketId, since } } —
 *    server-authoritative field locks
 *  - socketId: this connection's id (locks with this socketId are OURS)
 *  - setField / acquireLock / releaseLock: the editing protocol —
 *    focus → acquireLock, type → setField, blur → releaseLock
 *  - sessionExpected: a session is meant to be running here (caller enabled it
 *    and we have an endpoint). `sessionExpected && !synced` is the CONNECTING
 *    window — callers must not present editable UI in it (see below)
 *
 * Heartbeats: while we hold a lock, a 20s heartbeat keeps it alive against
 * the server's 45s TTL sweep (which exists to free locks from crashed tabs).
 *
 * Stateless message protocol — must mirror okf-sub collab/CollabService.js.
 */

const LOCK_HEARTBEAT_MS = 20_000;



/**
 * Blur → release is DEFERRED by this short grace window. The PRIMARY guard
 * against portal tooling (annotation lightbox, overlap picker) releasing the
 * lock mid-use is deterministic: CollabFieldEngagementContext — the tooling
 * declares "still working on this field" and blur simply doesn't release.
 * This grace window only covers the residual blips: focus hopping between
 * editor and popover, clicks on non-focusable regions. Any setField() on the
 * path re-arms it; re-focus/re-engage cancels it.
 */
const LOCK_RELEASE_GRACE_MS = 5_000;

/**
 * A blur that arrives while the WINDOW itself lost focus (document.hasFocus()
 * false) is not "done with this field" — it's alt-tab / a Slack ping / another
 * monitor. Mid-thought focus theft used to release the lock 5s later and the
 * writer came back to a field someone else could have grabbed. Keep the lock
 * on a long idle grace instead: coming back cancels it (the browser re-fires
 * focus on the still-active element → acquireLock), staying away frees the
 * field for others after this window. A HIDDEN tab may lose the lock sooner
 * via the server's 45s TTL (browsers throttle its heartbeats) — same outcome,
 * server-decided; on return, the focus re-fire re-acquires the field if
 * nobody took it (heartbeats deliberately never re-grant a lapsed lock).
 */
const WINDOW_BLUR_LOCK_RELEASE_MS = 90_000;

/**
 * "Saving..." must never be a lie. A pending path is normally confirmed
 * (flush:ok) within ~1-2s; if NOTHING has confirmed or failed for this long,
 * the server isn't answering (bounce, network hole) and the status flips to
 * "stalled" so the writer knows their edit hasn't landed yet — instead of an
 * eternal reassuring spinner over an unsaved edit. Any later flush:ok /
 * flush:error resolves it (reconnect resyncs the doc and flushes, so
 * stalled normally self-resolves to saved).
 */
const SAVE_STALL_TIMEOUT_MS = 10_000;

/** Value equality for outbox acknowledgement — undefined and null collapse
 * (setField normalizes undefined → null before writing). */
const sameValue = (a, b) =>
  JSON.stringify(a === undefined ? null : a) === JSON.stringify(b === undefined ? null : b);

export const useCollabDoc = ({ enabled, docType = "contentDoc", contentType, contentId }) => {
  const [status, setStatus] = useState("disconnected");
  const [synced, setSynced] = useState(false);
  const [fields, setFields] = useState(null);
  const [locks, setLocks] = useState({});
  const [socketId, setSocketId] = useState(null);
  // Server couldn't persist the live doc to Mongo (broadcast per failed
  // flush). Cleared on the next local edit — if the retry fails too, the
  // server re-broadcasts within ~1s; if it succeeds, the error stays gone.
  const [flushError, setFlushError] = useState(null);
  // "idle" | "saving" | "saved" | "stalled" | "error" — OUR edits'
  // persistence state. setField puts its (path, value) in the OUTBOX; a
  // server flush:ok acknowledges a path only when the shared doc actually
  // CONTAINS our value (see the handler). "stalled" = outbox entries
  // unconfirmed past the watchdog (see SAVE_STALL_TIMEOUT_MS).
  const [saveStatus, setSaveStatus] = useState("idle");

  const providerRef = useRef(null);
  const heartbeatsRef = useRef(new Map()); // valuePath -> intervalId
  const pendingReleasesRef = useRef(new Map()); // valuePath -> timeoutId
  // OUTBOX: valuePath -> the value we wrote, kept until the shared doc
  // provably carries it (value-matched flush:ok). Not just a Set of paths:
  // after a server restart the fresh server doc's load-writes are CONCURRENT
  // (in CRDT terms) with writes we made while disconnected, and the Y.Map
  // merge picks a winner by arbitrary client-id order — our offline write
  // can silently LOSE and vanish from every replica (observed in E2E round
  // 2: typed-during-outage text reverted out of the typist's own editor).
  // Keeping the VALUE lets us re-send it after resync, like any reliable
  // outbox: unacknowledged ⇒ retransmit; acknowledged ⇔ the doc has it.
  const pendingSavePathsRef = useRef(new Map());
  // Mirror of the socketId state for the provider's stateless handler — the
  // handler closes over the first render's state, so it must read through a
  // ref to compare incoming lock holders against OUR current connection.
  const socketIdRef = useRef(null);
  const saveWatchdogRef = useRef(null); // timeout: pending paths unconfirmed too long → "stalled"
  // valuePath -> () => boolean, provided by the field wrapper at acquire
  // time: "is the user still actually IN this field?" (focus inside the
  // wrapper, or portal tooling engaged). The lock lifecycle otherwise hangs
  // entirely on blur events — and focus can END with NO blur event when the
  // focused node is REMOVED from the DOM (the Lexical collab remount does
  // exactly that). Without this check, the heartbeat immortalizes a ZOMBIE
  // lock on a field nobody is in.
  const lockLivenessRef = useRef(new Map());

  const clearHeartbeat = useCallback((valuePath) => {
    const interval = heartbeatsRef.current.get(valuePath);
    if (interval) {
      clearInterval(interval);
      heartbeatsRef.current.delete(valuePath);
    }
    lockLivenessRef.current.delete(valuePath);
  }, []);

  const cancelPendingRelease = useCallback((valuePath) => {
    const timeout = pendingReleasesRef.current.get(valuePath);
    if (timeout) {
      clearTimeout(timeout);
      pendingReleasesRef.current.delete(valuePath);
    }
  }, []);

  const clearSaveWatchdog = useCallback(() => {
    if (saveWatchdogRef.current) {
      clearTimeout(saveWatchdogRef.current);
      saveWatchdogRef.current = null;
    }
  }, []);

  /** (Re)armed on every write and on every partial confirmation — each is
   * fresh evidence the pipe works, so the stall clock starts over. */
  const armSaveWatchdog = useCallback(() => {
    clearSaveWatchdog();
    saveWatchdogRef.current = setTimeout(() => {
      saveWatchdogRef.current = null;
      if (pendingSavePathsRef.current.size > 0) setSaveStatus("stalled");
    }, SAVE_STALL_TIMEOUT_MS);
  }, [clearSaveWatchdog]);

  // A session is EXPECTED here: the caller enabled it and we have somewhere to
  // connect to. Distinct from `synced` (it's up) — the window between the two is
  // where a caller must not present editable UI, because edits made in it would
  // be silently discarded when the Y.Doc lands. Derived, not state, so it flips
  // in the same render as `enabled`.
  const wsBase = getWebSocketEndpoint()?.replace(/^http/, "ws");
  const sessionExpected = !!(enabled && contentType && contentId && wsBase);

  useEffect(() => {
    if (!sessionExpected) return undefined;

    const tenant = getTenant();

    const provider = new HocuspocusProvider({
      url: `${wsBase}/collab`,
      name: `${docType}:${tenant}:${contentType}:${contentId}`,
      token: () => localStorage.token,
      onStatus: ({ status: s }) => setStatus(s),
      onSynced: () => setSynced(true),
      onStateless: ({ payload }) => {
        let message;
        try {
          message = JSON.parse(payload);
        } catch {
          return;
        }
        if (message?.type === "session:info") {
          socketIdRef.current = message.socketId;
          setSocketId(message.socketId);
        }
        if (message?.type === "locks:state") {
          const nextLocks = message.locks || {};
          // Yield: a path we're still renewing (heartbeat armed = we believe
          // we hold it) now shows ANOTHER socket as holder — our lease lapsed
          // (restart/TTL) and someone grabbed the field in the gap. Stop
          // renewing and close the setField write gate so our keystrokes
          // stop entering the doc over theirs; the locked-by-other overlay
          // comes from this same locks state. NOT a lock:release — the lock
          // isn't ours to release.
          heartbeatsRef.current.forEach((_interval, path) => {
            const holder = nextLocks[path];
            if (holder && socketIdRef.current && holder.socketId !== socketIdRef.current) {
              clearHeartbeat(path);
              cancelPendingRelease(path);
              // A field we lost to another editor is theirs now — an
              // unacknowledged write of ours must NOT be retransmitted over
              // their work. Dropping it is real (rare) data loss, so say so.
              if (pendingSavePathsRef.current.has(path)) {
                pendingSavePathsRef.current.delete(path);
                setFlushError(
                  `${holder.userName || "Another user"} took over a field before your last edit to it was saved — that edit was discarded`
                );
                setSaveStatus("error");
              }
            }
          });
          setLocks(nextLocks);
        }
        if (message?.type === "flush:ok") {
          // Acknowledge a path ONLY if the shared doc carries OUR value —
          // a flush:ok proves the doc state was persisted, not that our
          // write is part of it. Another client's flush of the same path
          // (e.g. its post-restart resync, which predates our offline
          // write) must not clear our outbox; and if the restart merge
          // dropped our value (see pendingSavePathsRef), re-send it — the
          // fresh write is causally after the merge, so it wins cleanly.
          const docFields = providerRef.current?.document.getMap("fields");
          (message.paths || []).forEach((p) => {
            if (!pendingSavePathsRef.current.has(p) || !docFields) return;
            const ours = pendingSavePathsRef.current.get(p);
            if (sameValue(docFields.get(p), ours)) {
              pendingSavePathsRef.current.delete(p);
            } else {
              docFields.set(p, ours === undefined ? null : ours);
            }
          });
          if (pendingSavePathsRef.current.size === 0) {
            clearSaveWatchdog();
            setSaveStatus((s) =>
              s === "saving" || s === "stalled" || s === "error" ? "saved" : s
            );
          } else {
            // partial confirmation = the pipe works; restart the stall clock
            armSaveWatchdog();
          }
        }
        if (message?.type === "flush:error") {
          clearSaveWatchdog(); // "error" is an explicit verdict, not a stall
          setFlushError(message.message || "save failed");
          setSaveStatus("error");
        }
      },
    });

    const fieldsMap = provider.document.getMap("fields");
    const onFieldsChange = () => setFields(fieldsMap.toJSON());
    fieldsMap.observe(onFieldsChange);
    // Also read the map once on sync. `observe` fires on CHANGE only, so a doc
    // that syncs EMPTY (a report whose Y.Doc has no fields yet) would never fire
    // it, and `fields` would sit at null forever — which reads as "still
    // connecting" to every caller and would hang their pending-gate.
    provider.on("synced", onFieldsChange);

    // Re-assert held locks on every (re)sync: the server's lock registry is
    // in-memory, so a service restart — or a socket bounce (disconnect
    // releases our locks server-side) — forgets us while our editor is still
    // focused. acquire is idempotent for the current holder and re-grants a
    // free field instantly; if someone grabbed the field in the gap, the
    // resulting locks:state broadcast makes us yield (see onStateless).
    // This reconnect event is the ONLY self-heal for a lost lock — the
    // server deliberately never re-grants on heartbeat (a hidden tab's
    // throttled pings would resurrect an abandoned lock forever); away from
    // a reconnect, recovery is the user refocusing the field.
    const reassertHeldLocks = () => {
      heartbeatsRef.current.forEach((_interval, valuePath) => {
        // Same presence rule as the heartbeat: don't re-assert a lock on a
        // field the user is no longer in (focus died blur-lessly while we
        // were disconnected) — release it instead.
        const alive = lockLivenessRef.current.get(valuePath);
        if (alive && !alive()) {
          doReleaseLock(valuePath);
          return;
        }
        provider.sendStateless(JSON.stringify({ type: "lock:acquire", valuePath }));
      });
      // Outbox retransmit: after the resync merge, any unacknowledged write
      // whose value is NOT in the doc lost the CRDT coin-flip against the
      // fresh server doc's load-writes — re-send it. This fresh write is
      // causally after the merge, so it wins deterministically.
      pendingSavePathsRef.current.forEach((ours, valuePath) => {
        if (!sameValue(fieldsMap.get(valuePath), ours)) {
          fieldsMap.set(valuePath, ours === undefined ? null : ours);
        }
      });
    };
    provider.on("synced", reassertHeldLocks);

    providerRef.current = provider;

    const heartbeats = heartbeatsRef.current;
    const pendingReleases = pendingReleasesRef.current;
    return () => {
      heartbeats.forEach((interval) => clearInterval(interval));
      heartbeats.clear();
      pendingReleases.forEach((timeout) => clearTimeout(timeout));
      pendingReleases.clear();
      fieldsMap.unobserve(onFieldsChange);
      provider.off("synced", onFieldsChange);
      provider.off("synced", reassertHeldLocks);
      provider.destroy();
      providerRef.current = null;
      pendingSavePathsRef.current.clear();
      if (saveWatchdogRef.current) {
        clearTimeout(saveWatchdogRef.current);
        saveWatchdogRef.current = null;
      }
      setFields(null);
      setLocks({});
      setSocketId(null);
      setSynced(false);
      setStatus("disconnected");
      setSaveStatus("idle");
    };
  }, [sessionExpected, docType, contentType, contentId, wsBase]);

  const sendStateless = useCallback((message) => {
    providerRef.current?.sendStateless(JSON.stringify(message));
  }, []);

  const doReleaseLock = useCallback(
    (valuePath) => {
      cancelPendingRelease(valuePath);
      clearHeartbeat(valuePath);
      sendStateless({ type: "lock:release", valuePath });
    },
    [sendStateless, clearHeartbeat, cancelPendingRelease]
  );

  /** Deferred release: called on blur. Re-armed by setField, canceled by
   * acquireLock (re-focus). The grace depends on WHY we blurred: moved on
   * within the page → short (LOCK_RELEASE_GRACE_MS); the whole window lost
   * focus → long (WINDOW_BLUR_LOCK_RELEASE_MS) — document.hasFocus() is
   * still false at blur-dispatch time, which is what tells the two apart. */
  const releaseLock = useCallback(
    (valuePath) => {
      cancelPendingRelease(valuePath);
      const graceMs = document.hasFocus()
        ? LOCK_RELEASE_GRACE_MS
        : WINDOW_BLUR_LOCK_RELEASE_MS;
      pendingReleasesRef.current.set(
        valuePath,
        setTimeout(() => doReleaseLock(valuePath), graceMs)
      );
    },
    [doReleaseLock, cancelPendingRelease]
  );

  const setField = useCallback(
    (valuePath, value) => {
      const fieldsMap = providerRef.current?.document.getMap("fields");
      if (!fieldsMap) return;
      const next = value === undefined ? null : value;
      // Lock gate — contentDoc only: every legitimate contentDoc write is
      // lock-bracketed (focus/engage → acquireLock → setField), so only
      // paths this client focus-acquired (heartbeat armed by acquireLock,
      // cleared after release) may write. reportDoc DOES lock widget edits,
      // but some of its write paths are legitimately lock-free (widget
      // reorder via __configOrder, title/meta via setMeta) — gating those
      // would silently drop real edits, so the gate skips reportDoc. What this
      // blocks: mount-time onChange from editor normalization — Lexical
      // rewrites version/direction/key-order on load, so the JSON echo
      // guard below sees a "change" and, before this gate, every doc-open
      // wrote a stale field snapshot into the live doc (flush then
      // clobbered concurrent AI-annotation applies and fired session side
      // effects that invalidated the doc-summary cache mid-run). This gate
      // is the ONLY line of defense against that noise: the server flushes
      // whatever reaches the live doc (flush follows the doc — it no longer
      // drops unlocked writes, that silently lost real edits whenever a
      // lock died invisibly), so noise must be stopped here at the source.
      if (docType === "contentDoc" && !heartbeatsRef.current.has(valuePath)) return;
      // Echo guard: comps fire onChange on mount (normalization, defaulting)
      // with the value they were just given. Without this, opening a page
      // marks EVERY editable path dirty → spurious flush + session side
      // effects for a doc nobody edited.
      if (JSON.stringify(fieldsMap.get(valuePath)) === JSON.stringify(next)) return;
      setFlushError(null); // fresh edit → fresh verdict from the next flush
      pendingSavePathsRef.current.set(valuePath, next);
      setSaveStatus("saving");
      armSaveWatchdog();
      fieldsMap.set(valuePath, next);
      // still working on the field (e.g. via a portaled lightbox after blur)
      // → push the pending release out (same focus-aware grace as releaseLock)
      if (pendingReleasesRef.current.has(valuePath)) {
        cancelPendingRelease(valuePath);
        const graceMs = document.hasFocus()
          ? LOCK_RELEASE_GRACE_MS
          : WINDOW_BLUR_LOCK_RELEASE_MS;
        pendingReleasesRef.current.set(
          valuePath,
          setTimeout(() => doReleaseLock(valuePath), graceMs)
        );
      }
    },
    [docType, doReleaseLock, cancelPendingRelease, armSaveWatchdog]
  );

  /** @param isStillHeldByUser optional wrapper-provided presence check (see
   * lockLivenessRef). Callers without one keep plain renew-forever pings. */
  /** Do WE currently claim this path (focus-acquired, not yet released)?
   * This is the client's own belief — same source the setField write gate
   * uses — and unlike `locks` it is TRUE synchronously at acquire time,
   * before the server's locks:state broadcast round-trips. Callers that
   * must decide "did MY interaction cause this?" in the same tick as the
   * interaction (the conditional-field cleanup actor rule) need this;
   * the broadcast state lags by a network hop. */
  const hasLockClaim = useCallback(
    (valuePath) => heartbeatsRef.current.has(valuePath),
    []
  );

  const acquireLock = useCallback(
    (valuePath, isStillHeldByUser) => {
      cancelPendingRelease(valuePath);
      sendStateless({ type: "lock:acquire", valuePath });
      if (isStillHeldByUser) lockLivenessRef.current.set(valuePath, isStillHeldByUser);
      if (!heartbeatsRef.current.has(valuePath)) {
        heartbeatsRef.current.set(
          valuePath,
          setInterval(() => {
            // A heartbeat vouches for PRESENCE, not just a live tab: if
            // focus left this field without a blur (node removed on
            // remount), release instead of renewing a zombie lock.
            const alive = lockLivenessRef.current.get(valuePath);
            if (alive && !alive()) {
              doReleaseLock(valuePath);
              return;
            }
            sendStateless({ type: "lock:heartbeat", valuePath });
          }, LOCK_HEARTBEAT_MS)
        );
      }
    },
    [sendStateless, cancelPendingRelease, doReleaseLock]
  );

  // "Saving..." with a dead socket is a lie the moment the socket drops —
  // that failure has an EVENT (provider status), so it needs no timer:
  // downgrade to "stalled" immediately. The watchdog only covers the case
  // no event can express: connected but the server has gone silent.
  const effectiveSaveStatus =
    saveStatus === "saving" && status !== "connected" ? "stalled" : saveStatus;

  return {
    connected: status === "connected",
    sessionExpected,
    synced,
    fields,
    locks,
    socketId,
    flushError,
    saveStatus: effectiveSaveStatus,
    setField,
    acquireLock,
    releaseLock,
    hasLockClaim,
  };
};
